import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Show from 'components/Form/Show';
import Branch from 'components/Form/Branch';
import DateRange from 'components/Form/DateRange';
import Telephone from 'components/Form/Telephone';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { SubstanceAlcoholOrderedByModel, SubstanceAlcoholOrderedCounselingItemModel } from 'forms/sections/substance/alcohol/counseling/mandatory/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import {
  ApplicableModel,
  CheckboxModel, CountryArrayModel, CountryModel, LocationModel
} from 'forms/shared/types';
import { Dispatch } from 'redux';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import NotApplicable from 'components/Form/NotApplicable';
import PVQName from 'components/Form/PVQName';
import TelephoneCollection from 'components/Form/Telephone/TelephoneCollection';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { isSafeInternational } from 'helpers/locations';
import OrderedBy from './OrderedBy';

interface Props
  extends Partial<SubstanceAlcoholOrderedCounselingItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  requireAlcoholOrderedCounselingParty?: boolean
  formType?: string
}

export default class OrderedCounselingItem extends ValidationElement<Props> {
  update = (values: Partial<SubstanceAlcoholOrderedCounselingItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        Seekers: this.props.Seekers,
        OtherSeeker: this.props.OtherSeeker,
        ActionTaken: this.props.ActionTaken,
        NoActionTakenExplanation: this.props.NoActionTakenExplanation,
        CounselingDates: this.props.CounselingDates,
        TreatmentProviderName: this.props.TreatmentProviderName,
        TreatmentProviderNameNotApplicable: this.props.TreatmentProviderNameNotApplicable,
        TreatmentProviderPersonName: this.props.TreatmentProviderPersonName,
        TreatmentProviderAddress: this.props.TreatmentProviderAddress,
        TreatmentProviderAddressNotApplicable: this.props.TreatmentProviderAddressNotApplicable,
        TreatmentProviderTelephone: this.props.TreatmentProviderTelephone,
        TreatmentProviderPhones: this.props.TreatmentProviderPhones,
        TreatmentProviderEmails: this.props.TreatmentProviderEmails,
        CompletedTreatment: this.props.CompletedTreatment,
        NoCompletedTreatmentExplanation: this.props.NoCompletedTreatmentExplanation,
        OrderedByComments: this.props.OrderedByComments,
        Comments: this.props.Comments,
        CompletedTreatmentComments: this.props.CompletedTreatmentComments,
        NoActionTakenExplanationComments: this.props.NoActionTakenExplanationComments,
        OrderedBy: this.props.OrderedBy,
        OrderedByExplanation: this.props.OrderedByExplanation,
        ...values,
      });
    }
  };

  /**
   * "Seekers" is the list of checkboxes of people that ordered counseling or treatment
   * because of alcohol use plus the "Not Ordered" and "Other" selections.
   * "lastSelection" is the last selection made; "selected" is the current list of selections
   */
  updateSeekers = (values: CheckboxModel) => {
    const lastSelection = values.value;
    let selected = [...(this.props.Seekers?.values || [])];

    if (selected.includes(lastSelection)) {
      // Remove the relation if it was previously selected
      selected.splice(selected.indexOf(lastSelection), 1);
    } else if (lastSelection === 'NotOrdered' || selected.includes('NotOrdered')) {
      selected = [lastSelection];
    } else {
      // Add the relation if it wasn't already
      selected.push(lastSelection);
    }
    this.update({ Seekers: { values: selected } });
  };

  updateOtherSeeker = (values: SubstanceAlcoholOrderedCounselingItemModel['OtherSeeker']) => {
    this.update({ OtherSeeker: values });
  };

  updateActionTaken = (values: SubstanceAlcoholOrderedCounselingItemModel['ActionTaken']) => {
    let additionalValues = {};
    if (values.value === 'Yes') {
      additionalValues = {
        NoActionTakenExplanation: {},
        NoActionTakenExplanationComments: {}
      };
    } else if (values.value === 'No') {
      additionalValues = {
        CounselingDates: {},
        TreatmentProviderName: {},
        TreatmentProviderAddress: {},
        TreatmentProviderTelephone: {},
        Comments: {},
        CompletedTreatment: {},
        NoCompletedTreatmentExplanation: {},
        CompletedTreatmentComments: {},
        TreatmentProviderAddressNotApplicable: {},
        TreatmentProviderPhones: {},
        TreatmentProviderEmails: {},
      };
    }

    this.update({ ActionTaken: values, ...additionalValues });
  };

  updateNoActionTakenExplanation = (
    values: SubstanceAlcoholOrderedCounselingItemModel['NoActionTakenExplanation']
  ) => {
    this.update({ NoActionTakenExplanation: values });
  };

  updateCounselingDates = (
    values: SubstanceAlcoholOrderedCounselingItemModel['CounselingDates']
  ) => {
    this.update({ CounselingDates: values });
  };

  updateTreatmentProviderName = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderName']
  ) => {
    this.update({ TreatmentProviderName: values });
  };

  updateTreatmentProviderNameNotApplicable = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderNameNotApplicable']
  ) => {
    const updatedName = values?.applicable === false ? {} : this.props.TreatmentProviderName;
    const updatedPersonName = values?.applicable === false ? {} : this.props.TreatmentProviderName;
    this.update({
      TreatmentProviderNameNotApplicable: values,
      TreatmentProviderName: updatedName,
      TreatmentProviderPersonName: updatedPersonName,
    });
  };

  updateTreatmentProviderPersonName = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderPersonName']
  ) => {
    this.update({ TreatmentProviderPersonName: values });
  };

  updateTreatmentProviderAddress = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderAddress']
  ) => {
    this.update({ TreatmentProviderAddress: values });
  };

  updateTreatmentProviderAddressNotApplicable = (treatmentProviderAddressNotApplicable: ApplicableModel) => {
    const queue: Partial<SubstanceAlcoholOrderedCounselingItemModel> = {
      TreatmentProviderAddressNotApplicable: { applicable: treatmentProviderAddressNotApplicable.applicable }
    };

    if (treatmentProviderAddressNotApplicable.applicable === false) {
      const treatmentProviderAddress: LocationModel | undefined = this.props.TreatmentProviderAddress;

      let country: CountryModel | string | CountryArrayModel | undefined = treatmentProviderAddress?.country;

      if (country) {
        if (isSafeInternational(treatmentProviderAddress)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.TreatmentProviderAddress = { country } as LocationModel;
      } else {
        queue.TreatmentProviderAddress = { } as LocationModel;
      }
    }

    this.update(queue);
  };

  updateTreatmentProviderTelephone = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderTelephone']
  ) => {
    this.update({ TreatmentProviderTelephone: values });
  };

  updateTreatmentProviderPhones = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderPhones']
  ) => {
    this.update({
      TreatmentProviderPhones: values,
    });
  };

  updateTreatmentProviderEmails = (
    values: SubstanceAlcoholOrderedCounselingItemModel['TreatmentProviderEmails']
  ) => {
    this.update({
      TreatmentProviderEmails: values,
    });
  };

  updateCompletedTreatment = (
    values: SubstanceAlcoholOrderedCounselingItemModel['CompletedTreatment']
  ) => {
    this.update({ CompletedTreatment: values });
  };

  updateNoCompletedTreatmentExplanation = (
    values: SubstanceAlcoholOrderedCounselingItemModel['NoCompletedTreatmentExplanation']
  ) => {
    this.update({ NoCompletedTreatmentExplanation: values });
  };

  updateComments = (
    values: SubstanceAlcoholOrderedCounselingItemModel['Comments']
  ) => {
    this.update({ Comments: values });
  };

  updateOrderedByComments = (
    values: SubstanceAlcoholOrderedCounselingItemModel['OrderedByComments']
  ) => {
    this.update({ OrderedByComments: values });
  };

  updateNoActionTakenExplanationComments = (
    values: SubstanceAlcoholOrderedCounselingItemModel['NoActionTakenExplanationComments']
  ) => {
    this.update({ NoActionTakenExplanationComments: values });
  };

  updateCompletedTreatmentComments = (
    values: SubstanceAlcoholOrderedCounselingItemModel['CompletedTreatmentComments']
  ) => {
    this.update({ CompletedTreatmentComments: values });
  };

  updateOrderedBy = (values: SubstanceAlcoholOrderedByModel) => {
    this.update({
      OrderedBy: values.OrderedBy,
      OrderedByExplanation: values.OrderedByExplanation,
    });
  };

  render() {
    const { requireAlcoholOrderedCounselingParty } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="ordered-counseling">
        <Field
          title={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.orderedBy')}
          attribute={`${attribute}.OrderedBy`}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          <OrderedBy
            {...{
              OrderedBy: this.props.OrderedBy,
              OrderedByExplanation: this.props.OrderedByExplanation,
            }}
            name="OrderedBy"
            onUpdate={this.updateOrderedBy}
            onError={this.props.onError}
            className="substance-alcohol-ordered-orderedby"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.OrderedBy`}
          />
        </Field>

        <Branch
          name="ActionTaken"
          attribute={`${attribute}.ActionTaken`}
          label={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.actionTaken')}
          labelSize="h4"
          className={`action-taken ${this.props.ActionTaken?.value === 'No' ? 'no-margin-bottom' : ''
          }`}
          {...this.props.ActionTaken}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateActionTaken}
          scrollIntoView={this.props.scrollIntoView}
          warning
        />
        {this.props.ActionTaken?.value === 'Yes'
        && (
        <div>
          <Field
            title={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.counselingDates')}
            attribute={`${attribute}.CounselingDates`}
            help="substance.pvq.alcohol.orderedCounseling.help.counselingDates"
            adjustFor="daterange"
            scrollIntoView={this.props.scrollIntoView}
          >
            <DateRange
              name="CounselingDates"
              attribute={`${attribute}.CounselingDates`}
              className="counseling-dates"
              minDateEqualTo
              {...this.props.CounselingDates}
              onUpdate={this.updateCounselingDates}
              onError={this.props.onError}
              required={this.props.required}
              hideFromDay
              hideToDay
            />
          </Field>

          <Field
            title={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.treatmentProviderName')}
            attribute={`${attribute}.TreatmentProviderName`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <NotApplicable
              {...this.props.TreatmentProviderNameNotApplicable}
              name="TreatmentProviderNameNotApplicable"
              className="substance-alcoholo-counseling-treatmentprovidernamenotapplicable"
              onUpdate={this.updateTreatmentProviderNameNotApplicable}
              onError={this.props.onError}
              or={i18n.m('substance.pvq.alcohol.orderedCounseling.para.or')}
              label={i18n.t('substance.pvq.alcohol.orderedCounseling.label.idk')}
              required={this.props.required}
              attribute={`${attribute}.TreatmentProviderNameNotApplicable`}
              includeNAConfirmation={Boolean(this.props.TreatmentProviderName?.value)}
            >
              <PVQName
                {...this.props.TreatmentProviderPersonName}
                attribute={`${attribute}.TreatmentProviderPersonName`}
                exactAttribute
                aria-label="Provide treatment provider person name"
                name="TreatmentProviderPersonName"
                onUpdate={this.updateTreatmentProviderPersonName}
                onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                showLastFirstMiddleNameOrder
                hideMiddleName
              />
            </NotApplicable>
          </Field>

          <TelephoneCollection
            {...this.props.TreatmentProviderPhones}
            onUpdate={this.updateTreatmentProviderPhones}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderPhones`}
            telephoneFieldTitle={i18n.m('substance.pvq.alcohol.orderedCounseling.heading.treatmentProviderTelephone')}
            showTimeOfDay
            allowNotApplicable
            hideInitialBranchQuestion
          />
          <EmailCollection
            {...this.props.TreatmentProviderEmails}
            onUpdate={this.updateTreatmentProviderEmails}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderEmails`}
            emailFieldTitle={i18n.m('substance.pvq.alcohol.orderedCounseling.heading.treatmentProviderEmail')}
            showEmailUnknown
            includeEmailType={false}
            hasSecondaryAppendLabels
            secondaryNoAppendLabel="No"
            secondaryYesAppendLabel="Add"
          />

          <Field
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TreatmentProviderAddress`}
            adjustFor="address"
          >
            <PVQLocationUnknown
              attribute={`${attribute}.TreatmentProviderAddress`}
              name="TreatmentProvider-address"
              {...this.props.TreatmentProviderAddress}
              addressBooks={this.props.addressBooks}
              addressBook="TreatmentProvider"
              addressUnknown={this.props.TreatmentProviderAddressNotApplicable}
              addressUnknownLabel={i18n.t('substance.pvq.alcohol.orderedCounseling.label.idk')}
              onUpdate={this.updateTreatmentProviderAddress} // eslint-disable-line @typescript-eslint/unbound-method
              onUpdateAddressUnknown={this.updateTreatmentProviderAddressNotApplicable}
              showAddressUnknown
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              dispatch={this.props.dispatch}
              required={this.props.required}
              geocode
              showApoFpoDpoPostcodeWhenForeign
              showInternationalStreet={false}
              showMilitaryInstallation
              label={i18n.t(`substance.pvq.alcohol.orderedCounseling.heading.address`)}
              domesticTitle={i18n.t(`substance.pvq.alcohol.orderedCounseling.heading.addressDomesticTitle`)}
              foreignTitle={i18n.t(`substance.pvq.alcohol.orderedCounseling.heading.addressForeignTitle`)}
              hideInstallationNameUnknownWhenForeign
            />
          </Field>

          <Branch
            name="CompletedTreatment"
            attribute={`${attribute}.CompletedTreatment`}
            label={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.completedTreatment')}
            labelSize="h4"
            className="completed-treatment"
            {...this.props.CompletedTreatment}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateCompletedTreatment}
            scrollIntoView={this.props.scrollIntoView}
          />

          {this.props.CompletedTreatment?.value === 'No'
          && (
          <Field
            title={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.noCompletedTreatment')}
            attribute={`${attribute}.NoCompletedTreatmentExplanation`}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="NoCompletedTreatmentExplanation"
              attribute={`${attribute}.NoCompletedTreatmentExplanation`}
              className="no-completed-treatment"
              {...this.props.NoCompletedTreatmentExplanation}
              onUpdate={this.updateNoCompletedTreatmentExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          )}
        </div>
        )}

        {this.props.ActionTaken?.value === 'No'
        && (
        <Field
          title={i18n.t('substance.pvq.alcohol.orderedCounseling.heading.noActionTakenExplanation')}
          attribute={`${attribute}.NoActionTakenExplanation`}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="NoActionTakenExplanation"
            attribute={`${attribute}.NoActionTakenExplanation`}
            {...this.props.NoActionTakenExplanation}
            onUpdate={this.updateNoActionTakenExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}
      </div>
    ) : (
      <div className="ordered-counseling">
        {requireAlcoholOrderedCounselingParty && (
          <Field
            title={i18n.t('substance.alcohol.orderedCounseling.heading.seekers')}
            attribute={`${attribute}.Seekers`}
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
          >
            {i18n.m('substance.alcohol.orderedCounseling.label.seekers')}
            <CheckboxGroup
              className="seekers option-list option-list-vertical"
              attribute={`${attribute}.Seekers`}
              onError={this.props.onError}
              required={this.props.required}
              selectedValues={(this.props.Seekers || {}).values || []}
            >
              <Checkbox
                name="seekers-employer"
                label={i18n.t('substance.alcohol.orderedCounseling.seekers.label.employer')}
                value="Employer"
                className="seekers-employer"
                onError={this.props.onError}
                onUpdate={this.updateSeekers}
              />

              <Checkbox
                name="seekers-medicalProfessional"
                label={i18n.t(
                  'substance.alcohol.orderedCounseling.seekers.label.medicalProfessional'
                )}
                value="MedicalProfessional"
                className="seekers-medical-professional"
                onError={this.props.onError}
                onUpdate={this.updateSeekers}
              />

              <Checkbox
                name="seekers-mentalHealthProfessional"
                label={i18n.t(
                  'substance.alcohol.orderedCounseling.seekers.label.mentalHealthProfessional'
                )}
                value="MentalHealthProfessional"
                className="seekers-mental-health-professional"
                onError={this.props.onError}
                onUpdate={this.updateSeekers}
              />

              <Checkbox
                name="seekers-courtOfficial"
                label={i18n.t('substance.alcohol.orderedCounseling.seekers.label.courtOfficial')}
                value="CourtOfficial"
                className="seekers-court-official"
                onError={this.props.onError}
                onUpdate={this.updateSeekers}
              />

              <Checkbox
                name="seekers-notordered"
                label={i18n.t('substance.alcohol.orderedCounseling.seekers.label.notOrdered')}
                value="NotOrdered"
                className="seekers-not-ordered"
                onError={this.props.onError}
                onUpdate={this.updateSeekers}
              />

              <Checkbox
                name="seekers-other"
                label={i18n.t('substance.alcohol.orderedCounseling.seekers.label.other')}
                value="Other"
                className="seekers-other"
                onError={this.props.onError}
                onUpdate={this.updateSeekers}
              />
            </CheckboxGroup>

            <Show when={((this.props.Seekers || {}).values || []).includes('Other')}>
              <Field
                title={i18n.t('substance.alcohol.orderedCounseling.label.otherSeeker')}
                attribute={`${attribute}.OtherSeeker`}
                titleSize="label"
                adjustFor="text"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Text
                  name="OtherSeeker"
                  attribute={`${attribute}.OtherSeeker`}
                  {...this.props.OtherSeeker}
                  onUpdate={this.updateOtherSeeker}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </Show>
          </Field>
        )}

        <Branch
          name="ActionTaken"
          attribute={`${attribute}.ActionTaken`}
          label={i18n.t('substance.alcohol.orderedCounseling.heading.actionTaken')}
          labelSize="h4"
          className={`action-taken ${this.props.ActionTaken?.value === 'No' ? 'no-margin-bottom' : ''
          }`}
          {...this.props.ActionTaken}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateActionTaken}
          scrollIntoView={this.props.scrollIntoView}
          warning
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="OrderedByComments"
          className="optional-comment"
          attribute="OrderedByComments"
          {...this.props.OrderedByComments}
          onUpdate={this.updateOrderedByComments}
          required={false}
        />
        <Show when={this.props.ActionTaken?.value === 'Yes'}>
          <div>
            <Field
              title={i18n.t('substance.alcohol.orderedCounseling.heading.counselingDates')}
              attribute={`${attribute}.CounselingDates`}
              help="substance.alcohol.orderedCounseling.help.counselingDates"
              adjustFor="daterange"
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateRange
                name="CounselingDates"
                attribute={`${attribute}.CounselingDates`}
                className="counseling-dates"
                minDateEqualTo
                {...this.props.CounselingDates}
                onUpdate={this.updateCounselingDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
              />
            </Field>
            <Field
              title={i18n.t('substance.alcohol.orderedCounseling.heading.treatmentProviderName')}
              attribute={`${attribute}.TreatmentProviderName`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                name="TreatmentProviderName"
                attribute={`${attribute}.TreatmentProviderName`}
                className="treatment-provider-name"
                {...this.props.TreatmentProviderName}
                onUpdate={this.updateTreatmentProviderName}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Field
              title={i18n.t('substance.alcohol.orderedCounseling.heading.treatmentProviderAddress')}
              attribute={`${attribute}.TreatmentProviderAddress`}
              optional
              help="substance.alcohol.orderedCounseling.help.treatmentProviderAddress"
              adjustFor="address"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Location
                name="TreatmentProviderAddress"
                attribute={`${attribute}.TreatmentProviderAddress`}
                className="provider-address"
                {...this.props.TreatmentProviderAddress}
                layout={Location.ADDRESS}
                geocode
                addressBooks={this.props.addressBooks}
                addressBook="Provider"
                showPostOffice
                dispatch={this.props.dispatch}
                onUpdate={this.updateTreatmentProviderAddress}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Field
              title={i18n.t(
                'substance.alcohol.orderedCounseling.heading.treatmentProviderTelephone'
              )}
              attribute={`${attribute}.TreatmentProviderTelephone`}
              help="substance.alcohol.orderedCounseling.help.treatmentProviderTelephone"
              className="override-required"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Telephone
                name="TreatmentProviderTelephone"
                attribute={`${attribute}.TreatmentProviderTelephone`}
                className="provider-telephone"
                {...this.props.TreatmentProviderTelephone}
                allowNotApplicable={false}
                onUpdate={this.updateTreatmentProviderTelephone}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
            <Comments
              title={i18n.t('substance.comments.optional')}
              name="Comments"
              className="optional-comment"
              attribute="Comments"
              {...this.props.Comments}
              onUpdate={this.updateComments}
              required={false}
            />
            <Branch
              name="CompletedTreatment"
              attribute={`${attribute}.CompletedTreatment`}
              label={i18n.t('substance.alcohol.orderedCounseling.heading.completedTreatment')}
              labelSize="h4"
              className="completed-treatment"
              {...this.props.CompletedTreatment}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateCompletedTreatment}
              scrollIntoView={this.props.scrollIntoView}
            />

            <Show when={this.props.CompletedTreatment?.value === 'No'}>
              <Field
                title={i18n.t('substance.alcohol.orderedCounseling.heading.noCompletedTreatment')}
                attribute={`${attribute}.NoCompletedTreatmentExplanation`}
                titleSize="label"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  name="NoCompletedTreatmentExplanation"
                  attribute={`${attribute}.NoCompletedTreatmentExplanation`}
                  className="no-completed-treatment"
                  {...this.props.NoCompletedTreatmentExplanation}
                  onUpdate={this.updateNoCompletedTreatmentExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
              <Comments
                title={i18n.t('substance.comments.optional')}
                name="CompletedTreatmentComments"
                className="optional-comment"
                attribute="CompletedTreatmentComments"
                {...this.props.CompletedTreatmentComments}
                onUpdate={this.updateCompletedTreatmentComments}
                required={false}
              />
            </Show>
          </div>
        </Show>

        <Show when={this.props.ActionTaken?.value === 'No'}>
          <Field
            title={i18n.t('substance.alcohol.orderedCounseling.heading.noActionTakenExplanation')}
            attribute={`${attribute}.NoActionTakenExplanation`}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="NoActionTakenExplanation"
              attribute={`${attribute}.NoActionTakenExplanation`}
              {...this.props.NoActionTakenExplanation}
              onUpdate={this.updateNoActionTakenExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          <Comments
            title={i18n.t('substance.comments.optional')}
            name="NoActionTakenExplanationComments"
            className="optional-comment"
            attribute="NoActionTakenExplanationComments"
            {...this.props.NoActionTakenExplanationComments}
            onUpdate={this.updateNoActionTakenExplanationComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

OrderedCounselingItem.defaultProps = {
  ActionTaken: {},
  CompletedTreatment: {},
  OrderedByComments: {},
  Comments: {},
  CompletedTreatmentComments: {},
  NoActionTakenExplanationComments: {},
  OrderedBy: {}, // PVQ Only
  OrderedByExplanation: {}, // PVQ Only
  TreatmentProviderNameNotApplicable: { applicable: true }, // PVQ Only
  TreatmentProviderPersonName: {}, // PVQ Only
  TreatmentProviderPhones: {}, // PVQ Only
  TreatmentProviderEmails: {}, // PVQ Only
  TreatmentProviderAddress: {},
  TreatmentProviderAddressNotApplicable: { applicable: true }, // PVQ Only
  addressBooks: {},
  dispatch: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
  requireAlcoholOrderedCounselingParty: true,
};
